/** Shared Wallex-pattern UI primitives for Nanom Wallet. */
import type { ReactNode } from "react";

export function ChipTab({
  active,
  onClick,
  children,
  tone = "accent",
}: {
  active?: boolean;
  onClick?: () => void;
  children: ReactNode;
  tone?: "accent" | "buy" | "sell" | "neutral";
}) {
  return (
    <button
      type="button"
      className={`wx-chip ${active ? `active tone-${tone}` : ""}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function SideTabs({
  side,
  onChange,
}: {
  side: "buy" | "sell";
  onChange: (s: "buy" | "sell") => void;
}) {
  return (
    <div className="wx-side-tabs">
      <button
        type="button"
        className={`wx-side buy ${side === "buy" ? "active" : ""}`}
        onClick={() => onChange("buy")}
      >
        خرید
      </button>
      <button
        type="button"
        className={`wx-side sell ${side === "sell" ? "active" : ""}`}
        onClick={() => onChange("sell")}
      >
        فروش
      </button>
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const opts =
    value && !options.some((o) => o.value === value)
      ? [{ value, label: value }, ...options]
      : options.length
        ? options
        : value
          ? [{ value, label: value }]
          : [{ value: "", label: "—" }];
  return (
    <label className="wx-field">
      <span className="wx-label">{label}</span>
      <select
        className="wx-select ltr"
        value={value || opts[0]?.value || ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {opts.map((o) => (
          <option key={o.value || "_"} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TextField({
  label,
  value,
  onChange,
  inputMode,
  suffix,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputMode?: "decimal" | "numeric" | "text";
  suffix?: string;
}) {
  return (
    <label className="wx-field">
      <span className="wx-label">{label}</span>
      <div className="wx-input-wrap">
        <input
          className="wx-input ltr"
          value={value}
          inputMode={inputMode || "decimal"}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix ? <span className="wx-suffix">{suffix}</span> : null}
      </div>
    </label>
  );
}

export function PercentSlider({
  value,
  onChange,
  tone = "buy",
}: {
  value: number;
  onChange: (v: number) => void;
  tone?: "buy" | "sell";
}) {
  const chips = [25, 50, 75, 100];
  return (
    <div className={`wx-pct tone-${tone}`} dir="ltr">
      <div className="wx-pct-chips" role="group" aria-label="percent">
        {chips.map((m) => (
          <button
            key={m}
            type="button"
            className={`wx-pct-chip${value === m ? " on" : ""}`}
            onClick={() => onChange(m)}
          >
            {m}%
          </button>
        ))}
      </div>
      <div className="wx-pct-rail">
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
        />
        <div className="wx-pct-scale" aria-hidden="true">
          <span>0</span>
          <span>25</span>
          <span>50</span>
          <span>75</span>
          <span>100</span>
        </div>
      </div>
    </div>
  );
}

export function DepthRow({
  side,
  price,
  qty,
  depthPct,
}: {
  side: "bid" | "ask";
  price: string;
  qty: string;
  depthPct: number;
}) {
  return (
    <div className={`wx-depth-row ${side}`}>
      <span className="wx-depth-bar" style={{ width: `${Math.min(100, Math.max(4, depthPct))}%` }} />
      <span className="wx-depth-price ltr">{price}</span>
      <span className="wx-depth-qty ltr">{qty}</span>
    </div>
  );
}

export function StatusChip({ children, tone = "muted" }: { children: ReactNode; tone?: string }) {
  return <span className={`wx-status tone-${tone}`}>{children}</span>;
}
